import { useState, useEffect, useCallback } from "react";
import { STRINGS } from "../../../constants/strings";
import { loginRequest, fetchOrganizationsRequest } from "../api/authApi";
import type { Organization } from "../../../types";

export const useAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [requireOrgSelection, setRequireOrgSelection] = useState<boolean>(false);

  const logout = useCallback(() => {
    localStorage.removeItem("strapi_jwt");
    localStorage.removeItem("organization_id");
    setIsAuthenticated(false);
    setOrganizations([]);
    setRequireOrgSelection(false);
  }, []);

  const selectOrganization = (orgId: string) => {
    localStorage.setItem("organization_id", orgId);
    setRequireOrgSelection(false);
    setIsAuthenticated(true);
  };

  useEffect(() => {
    const handleUnauthorized = () => logout();
    window.addEventListener("unauthorized", handleUnauthorized);

    const token = localStorage.getItem("strapi_jwt");
    const orgId = localStorage.getItem("organization_id");
    if (token) {
      try {
        const payloadBase64 = token.split('.')[1];
        if (payloadBase64) {
          const payload = JSON.parse(atob(payloadBase64));
          const isExpired = payload.exp && payload.exp * 1000 < Date.now();
          if (isExpired) {
            logout();
          } else {
            if (orgId) {
              setIsAuthenticated(true);
            } else {
              setLoading(true);
              fetchOrganizationsRequest(token)
                .then(orgs => {
                  if (orgs.length === 1) {
                    selectOrganization(orgs[0].documentId);
                  } else if (orgs.length > 1) {
                    setOrganizations(orgs);
                    setRequireOrgSelection(true);
                  } else {
                    setError(STRINGS.AUTH.USER_ORGANIZATIONS_NOT_FOUND);
                    logout();
                  }
                })
                .catch(() => logout())
                .finally(() => setLoading(false));
            }
          }
        } else {
          // Fallback if no payload
          if (orgId) setIsAuthenticated(true);
          else logout();
        }
      } catch (e) {
        if (orgId) setIsAuthenticated(true);
        else logout();
      }
    }

    return () => {
      window.removeEventListener("unauthorized", handleUnauthorized);
    };
  }, [logout]);

  const login = async (email: string, password: string) => {
    setError(null);
    setLoading(true);

    try {
      const data = await loginRequest(email, password);
      localStorage.setItem("strapi_jwt", data.jwt);
      
      const orgs = await fetchOrganizationsRequest(data.jwt);
      if (orgs.length === 0) {
        throw new Error(STRINGS.AUTH.USER_ORGANIZATIONS_NOT_FOUND);
      } else if (orgs.length === 1) {
        selectOrganization(orgs[0].documentId);
      } else {
        setOrganizations(orgs);
        setRequireOrgSelection(true);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : STRINGS.AUTH.LOGIN_ERROR_FALLBACK);
      // Clean up in case jwt was set but orgs failed
      localStorage.removeItem("strapi_jwt");
    } finally {
      setLoading(false);
    }
  };

  return { isAuthenticated, login, logout, loading, error, requireOrgSelection, organizations, selectOrganization };
};
